import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import apiService from '../services/apiService'
import { Play } from 'lucide-react'
import Modal from '../ui/Modal'

function AddLesson({ onClose, courseId, categories = [], defaultCategoryId = null }) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm()
  const api = apiService()
  const [selectedVideo, setSelectedVideo] = useState(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [isUploading, setIsUploading] = useState(false)
  const [showVideoBrowser, setShowVideoBrowser] = useState(false)
  const [cloudflareVideos, setCloudflareVideos] = useState([])
  const [isLoadingVideos, setIsLoadingVideos] = useState(false)
  const [selectedCloudflareVideo, setSelectedCloudflareVideo] = useState(null)
  const [isUploadingToCloudflare, setIsUploadingToCloudflare] = useState(false)
  const [cloudflareUploadProgress, setCloudflareUploadProgress] = useState(0)
  
  const videoUrl = watch('videoUrl')

  // Safeguard: ensure cloudflareVideos is always an array
  const safeSetCloudflareVideos = (videos) => {
    if (Array.isArray(videos)) {
      setCloudflareVideos(videos)
    } else {
      console.error('Attempted to set non-array to cloudflareVideos:', videos)
      setCloudflareVideos([])
    }
  }

  const handleVideoChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      // Validate file type
      const validTypes = ['video/mp4', 'video/mpeg', 'video/quicktime', 'video/x-msvideo', 'video/x-ms-wmv', 'video/webm', 'video/ogg']
      if (!validTypes.includes(file.type)) {
        toast.error('Invalid file type. Please select a video file.')
        e.target.value = ''
        return
      }
      // Validate file size (500MB)
      if (file.size > 500 * 1024 * 1024) {
        toast.error('File size too large. Maximum size is 500MB.')
        e.target.value = ''
        return
      }
      setSelectedVideo(file)
      setSelectedCloudflareVideo(null)
      setValue('videoUrl', '')
    }
  }

  const fetchCloudflareVideos = async () => {
    if (isLoadingVideos) return // Prevent multiple simultaneous requests
    setIsLoadingVideos(true)
    safeSetCloudflareVideos([]) // Clear previous videos
    try {
      const response = await api.get('cloudflare/videos', {
        params: { per_page: 50 }
      })
      
      // Check if response.data exists and is not an error object
      if (response && response.data) {
        // Check if it's an error object (has error keys) - these should never be set to state
        if (response.data.errorReasonCode || response.data.errorReasonText || 
            (response.data.state && !response.data.success) || 
            response.data.pctComplete !== undefined) {
          console.error('Cloudflare API error:', response.data)
          const errorMsg = response.data.errorReasonText || response.data.message || 'Failed to fetch videos from Cloudflare'
          toast.error(errorMsg)
          safeSetCloudflareVideos([])
          return
        }
        
        // Check if it's a success response with videos array
        if (response.data.success === true && Array.isArray(response.data.videos)) {
          // Ensure all items in the array are valid video objects
          const validVideos = response.data.videos.filter(v => 
            v && typeof v === 'object' && v.uid && !v.errorReasonCode && !v.errorReasonText
          )
          safeSetCloudflareVideos(validVideos)
        } else if (response.data.message) {
          // API returned an error message
          toast.error(response.data.message)
          safeSetCloudflareVideos([])
        } else {
          // Unexpected response structure
          console.error('Unexpected response structure:', response.data)
          toast.error('Unexpected response from server')
          safeSetCloudflareVideos([])
        }
      } else {
        toast.error('No data received from server')
        safeSetCloudflareVideos([])
      }
    } catch (error) {
      console.error('Error fetching Cloudflare videos:', error)
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.errorReasonText ||
                          error.message || 
                          'Failed to fetch videos from Cloudflare. Please check your configuration.'
      toast.error(errorMessage)
      safeSetCloudflareVideos([])
    } finally {
      setIsLoadingVideos(false)
    }
  }

  const handleOpenVideoBrowser = () => {
    setShowVideoBrowser(true)
    fetchCloudflareVideos()
  }

  const handleSelectVideo = (video) => {
    setSelectedCloudflareVideo(video)
    // Use embed URL for better compatibility
    setValue('videoUrl', video.playback.embed)
    setValue('videoDuration', video.duration || 0)
    setSelectedVideo(null)
    setShowVideoBrowser(false)
    toast.success('Video selected!')
  }

  const handleUploadToCloudflare = async () => {
    if (!selectedVideo) {
      toast.error('Please select a video file first')
      return
    }

    try {
      setIsUploadingToCloudflare(true)
      setCloudflareUploadProgress(0)

      const formData = new FormData()
      formData.append('video', selectedVideo)
      formData.append('name', selectedVideo.name)

      const response = await api.uploadFile(
        'cloudflare/upload',
        formData,
        (progress) => setCloudflareUploadProgress(progress)
      )

      if (response.status === 200 && response.data.success && response.data.video) {
        const uploadedVideo = response.data.video
        setSelectedCloudflareVideo(uploadedVideo)
        setValue('videoUrl', uploadedVideo.playback.embed)
        setValue('videoDuration', uploadedVideo.duration || 0)
        setSelectedVideo(null)
        toast.success('Video uploaded to Cloudflare successfully!')
        // Refresh the video list
        fetchCloudflareVideos()
      } else {
        toast.error(response.data?.message || 'Failed to upload video to Cloudflare')
      }
    } catch (error) {
      console.error('Error uploading to Cloudflare:', error)
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to upload video to Cloudflare'
      toast.error(errorMessage)
    } finally {
      setIsUploadingToCloudflare(false)
      setCloudflareUploadProgress(0)
    }
  }


  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return 'N/A'
    // Round up to next integer as per requirement
    const totalSeconds = Math.ceil(parseFloat(seconds) || 0)
    const mins = Math.floor(totalSeconds / 60)
    const secs = totalSeconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const onSubmit = async (values) => {
    try {
      // First, create the lesson
      const response = await api.post('lessons', {
        title: values.title,
        description: values.description || '',
        videoUrl: values.videoUrl || '',
        videoDuration: values.videoDuration ? Math.ceil(parseFloat(values.videoDuration)) : 0,
        courseId: courseId,
        categoryId: values.categoryId || null,
        status: values.status || 'draft',
      })

      if (response.status === 200 || response.status === 201) {
        const lessonId = response.data.lesson?._id || response.data.lesson?.id
        
        // If a video file is selected, upload it
        if (selectedVideo && lessonId) {
          setIsUploading(true)
          setUploadProgress(0)
          
          const formData = new FormData()
          formData.append('video', selectedVideo)
          if (values.videoDuration) {
            formData.append('videoDuration', Math.ceil(parseFloat(values.videoDuration)))
          }

          try {
            await api.uploadFile(
              `lessons/${lessonId}/video`,
              formData,
              (progress) => setUploadProgress(progress)
            )
            toast.success('Lesson and video uploaded successfully')
          } catch (uploadError) {
            console.error('Error uploading video:', uploadError)
            toast.warning('Lesson created but video upload failed. You can upload it later.')
          } finally {
            setIsUploading(false)
            setUploadProgress(0)
          }
        } else {
          toast.success('Lesson created successfully')
        }
        
        if (onClose) onClose()
      }
    } catch (error) {
      console.error('Error creating lesson:', error)
      toast.error('Failed to create lesson')
    }
  }

  return (
    <form
      className="w-full mx-auto p-0"
      onSubmit={handleSubmit(onSubmit)}
      autoComplete="off"
    >
      <div className="grid grid-cols-1 gap-2.5 mb-2.5">
        <div>
          <label className="block text-[#030303] font-medium mb-1">
            Lesson Title<span className="text-[#DE3C7D]">*</span>
          </label>
          <input
            type="text"
            className="w-full bg-[#FAFAFA] border-none rounded-xl px-4 py-2 text-[#030303] font-medium focus:ring-2 focus:ring-[#DE3C7D] outline-none"
            {...register('title', { required: 'Lesson title is required' })}
            placeholder="Enter lesson title"
          />
          {errors.title && (
            <p className="text-[#DE3C7D] text-xs mt-1">
              {errors.title.message}
            </p>
          )}
        </div>
        <div>
          <label className="block text-[#030303] font-medium mb-1">
            Description
          </label>
          <textarea
            rows={2}
            className="w-full bg-[#FAFAFA] border-none rounded-xl px-4 py-2 text-[#030303] font-medium focus:ring-2 focus:ring-[#DE3C7D] outline-none resize-none"
            {...register('description')}
            placeholder="Enter lesson description"
          />
        </div>
        {categories.length > 0 && (
          <div>
            <label className="block text-[#030303] font-medium mb-1">
              Category (section)
            </label>
            <select
              className="w-full bg-[#FAFAFA] border-none rounded-xl px-4 py-2 text-[#030303] font-medium focus:ring-2 focus:ring-[#DE3C7D] outline-none"
              {...register('categoryId')}
              defaultValue={defaultCategoryId || ''}
            >
              <option value="">Uncategorized</option>
              {categories.map((c) => (
                <option key={c._id} value={c._id}>{c.title}</option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="block text-[#030303] font-medium mb-1">
            Video
          </label>
          <div className="space-y-1.5">
            <div>
              <label className="block text-sm text-[#535862] mb-1">
                Upload Video File (MP4, MOV, AVI, WMV, WEBM, OGG - Max 500MB)
              </label>
              <input
                type="file"
                accept="video/*"
                onChange={handleVideoChange}
                className="w-full bg-[#FAFAFA] border-none rounded-xl px-4 py-2 text-[#030303] font-medium focus:ring-2 focus:ring-[#DE3C7D] outline-none file:mr-4 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-[#DE3C7D] file:text-white hover:file:bg-[#c8356f] cursor-pointer"
              />
              {selectedVideo && (
                <div className="mt-1">
                  <p className="text-xs text-[#535862] mb-1">
                    Selected: {selectedVideo.name} ({(selectedVideo.size / (1024 * 1024)).toFixed(2)} MB)
                  </p>
                  <button
                    type="button"
                    onClick={handleUploadToCloudflare}
                    disabled={isUploadingToCloudflare || isUploading}
                    className="w-full bg-blue-600 text-white px-3 py-1 rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
                  >
                    {isUploadingToCloudflare ? (
                      <>
                        <span className="animate-spin">⏳</span>
                        Uploading to Cloudflare...
                      </>
                    ) : (
                      <>
                        <span>☁️</span>
                        Upload to Cloudflare
                      </>
                    )}
                  </button>
                  {isUploadingToCloudflare && (
                    <div className="mt-1">
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div
                          className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                          style={{ width: `${cloudflareUploadProgress}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-[#535862] mt-0.5">Uploading: {cloudflareUploadProgress}%</p>
                    </div>
                  )}
                </div>
              )}
              {isUploading && (
                <div className="mt-1">
                  <div className="w-full bg-gray-200 rounded-full h-1.5">
                    <div
                      className="bg-[#DE3C7D] h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-[#535862] mt-0.5">Uploading: {uploadProgress}%</p>
                </div>
              )}
            </div>
            <div className="text-center text-xs text-[#535862] py-0.5">OR</div>
            <div>
              <label className="block text-sm text-[#535862] mb-1">
                Select from Cloudflare Videos
              </label>
              <button
                type="button"
                onClick={handleOpenVideoBrowser}
                disabled={!!selectedVideo || isUploadingToCloudflare}
                className="w-full bg-[#DE3C7D] text-white px-4 py-2 rounded-xl font-medium hover:bg-[#c8356f] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <Play className="w-4 h-4" />
                Browse Cloudflare Videos
              </button>
              {selectedCloudflareVideo && (
                <div className="mt-1 p-1.5 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-xs text-green-800 font-medium">
                    Selected: {selectedCloudflareVideo.meta?.name || selectedCloudflareVideo.uid}
                  </p>
                  <p className="text-xs text-green-600 mt-0.5">
                    Duration: {formatDuration(selectedCloudflareVideo.duration)}
                  </p>
                </div>
              )}
            </div>
            <div className="text-center text-xs text-[#535862] py-0.5">OR</div>
            <div>
              <label className="block text-sm text-[#535862] mb-1">
                Enter Video URL (External Link)
              </label>
              <input
                type="url"
                className="w-full bg-[#FAFAFA] border-none rounded-xl px-4 py-2 text-[#030303] font-medium focus:ring-2 focus:ring-[#DE3C7D] outline-none"
                {...register('videoUrl')}
                placeholder="https://customer-xxxxx.cloudflarestream.com/xxxxx/video.mp4 or https://iframe.videodelivery.net/xxxxx"
                disabled={!!selectedVideo}
              />
              <p className="text-xs text-[#535862] mt-0.5">
                Supports: Cloudflare Stream, YouTube, Vimeo, or direct video URLs (MP4, WebM, etc.)
              </p>
            </div>
          </div>
        </div>
        <div>
          <label className="block text-[#030303] font-medium mb-1">
            Video Duration (seconds)
          </label>
          <input
            type="number"
            min="0"
            step="0.1"
            className="w-full bg-[#FAFAFA] border-none rounded-xl px-4 py-2 text-[#030303] font-medium focus:ring-2 focus:ring-[#DE3C7D] outline-none"
            {...register('videoDuration')}
            placeholder="0"
          />
        </div>
        <div>
          <label className="block text-[#030303] font-medium mb-1">
            Status<span className="text-[#DE3C7D]">*</span>
          </label>
          <div className="relative">
            <select
              className="w-full bg-[#FAFAFA] border-none rounded-xl px-4 py-2.5 pr-10 text-[#030303] font-medium focus:ring-2 focus:ring-[#DE3C7D] outline-none appearance-none cursor-pointer transition-all duration-200 hover:bg-[#F5F5F5]"
              {...register('status', { required: 'Status is required' })}
              defaultValue="draft"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23535862' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 16px center',
                backgroundSize: '12px',
                paddingRight: '40px'
              }}
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          {errors.status && (
            <p className="text-[#DE3C7D] text-xs mt-1">
              {errors.status.message}
            </p>
          )}
        </div>
      </div>
      <div className="flex flex-col md:flex-row gap-2.5 mt-2.5">
        <button
          type="button"
          onClick={onClose}
          className="w-full md:w-1/2 px-4 py-2 rounded-xl border border-[#E0E0E0] text-[#030303] bg-white font-medium hover:bg-neutral-50 transition-colors text-sm"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="w-full md:w-1/2 px-4 py-2 rounded-xl bg-[#DE3C7D] text-white font-medium hover:bg-[#c8356f] transition-colors text-sm"
        >
          Create Lesson
        </button>
      </div>

      {/* Cloudflare Video Browser Modal */}
      <Modal
        isOpen={showVideoBrowser}
        onClose={() => {
          setShowVideoBrowser(false)
        }}
        title="Select Video from Cloudflare"
        size="large"
      >
        <div className="p-4">
          {/* Video List */}
          {isLoadingVideos ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#DE3C7D]"></div>
              <p className="text-[#535862] mt-4">Loading videos...</p>
            </div>
          ) : !Array.isArray(cloudflareVideos) || cloudflareVideos.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[#535862]">No videos found. Try a different search term.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[500px] overflow-y-auto">
              {cloudflareVideos.filter(video => {
                // Strict validation: ensure it's a valid video object, not an error object
                return video && 
                       typeof video === 'object' && 
                       video.uid && 
                       !video.errorReasonCode && 
                       !video.errorReasonText && 
                       video.pctComplete === undefined &&
                       (video.state === undefined || video.state === 'ready')
              }).map((video) => (
                <div
                  key={video.uid}
                  onClick={() => handleSelectVideo(video)}
                  className="bg-white border border-[#F0F0F0] rounded-lg overflow-hidden cursor-pointer hover:border-[#DE3C7D] hover:shadow-md transition-all"
                >
                  <div className="relative aspect-video bg-gray-100">
                    {video.thumbnail ? (
                      <img
                        src={video.thumbnail}
                        alt={video.meta?.name || video.uid}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/320x180?text=No+Thumbnail'
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-200">
                        <Play className="w-12 h-12 text-gray-400" />
                      </div>
                    )}
                    <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                      {formatDuration(video.duration)}
                    </div>
                  </div>
                  <div className="p-3">
                    <p className="text-sm font-medium text-[#030303] truncate">
                      {String(video.meta?.name || video.uid || 'Unknown Video')}
                    </p>
                    <p className="text-xs text-[#535862] mt-1">
                      Status: {typeof video.status === 'object' ? (video.status?.state || video.status?.status || 'N/A') : String(video.status || 'N/A')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </form>
  )
}

export default AddLesson

